import { Injectable, ErrorHandler, inject } from '@angular/core';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class GlobalErrorHandlerService implements ErrorHandler {
  private logger = inject(LoggerService);

  handleError(error: Error): void {
    // Check if this is a browser extension error
    if (this.isBrowserExtensionError(error)) {
      this.logger.debug('Ignoring browser extension error', {
        message: error.message,
        stack: error.stack
      });
      return;
    }

    // Check if this is a non-critical error
    if (this.isNonCriticalError(error)) {
      this.logger.warn('Non-critical error occurred', {
        message: error.message,
        stack: error.stack
      });
      return;
    }

    // Log critical errors
    this.logger.error('Critical error occurred', {
      message: error.message,
      stack: error.stack
    });

    // In production, you might want to send this to an error reporting service
    if (this.shouldReportError(error)) {
      this.reportError(error);
    }
  }

  private isBrowserExtensionError(error: Error): boolean {
    const message = error.message.toLowerCase();
    const stack = error.stack?.toLowerCase() || '';

    return (
      message.includes('content.js') ||
      stack.includes('content.js') ||
      message.includes('chrome-extension') ||
      stack.includes('chrome-extension') ||
      message.includes('moz-extension') ||
      stack.includes('moz-extension') ||
      message.includes('chrome-error://') ||
      stack.includes('chrome-error://') ||
      message.includes('extension') ||
      stack.includes('extension')
    );
  }

  private isNonCriticalError(error: Error): boolean {
    const message = error.message.toLowerCase();

    // Network errors that are not critical
    const nonCriticalPatterns = [
      'network error',
      'connection refused',
      'timeout',
      'aborted',
      'cancelled',
      'blocked by client',
      'failed to fetch',
      'net::err_'
    ];

    return nonCriticalPatterns.some(pattern => message.includes(pattern));
  }

  private shouldReportError(error: Error): boolean {
    // Don't report browser extension errors
    if (this.isBrowserExtensionError(error)) {
      return false;
    }

    // Don't report non-critical errors
    if (this.isNonCriticalError(error)) {
      return false;
    }

    // Report all other errors
    return true;
  }

  private reportError(error: Error): void {
    // TODO: Implement error reporting to external service
    // Example: Sentry, LogRocket, etc.
    this.logger.error('Error should be reported to external service', {
      message: error.message,
      stack: error.stack
    });
  }
}
